import type { CasoDaFila, Investigacao } from '../../auditoria';
import { Icone } from '../../design/Icone';
import { quandoBR } from './comum';
import { comSinalUn, montarResumo } from './resumo-da-conferencia';

/**
 * RESUMO DA DIVERGÊNCIA (Carlos, 14/10/2026): quatro números e a diferença que abriu o caso,
 * acima da pergunta da prateleira. Abre na hora com o que a fila trouxe; o saldo ao vivo e as
 * movimentações do HOS chegam depois, sem segurar a contagem.
 */

function Numero({
  rotulo,
  valor,
  vazio,
  nota,
  destaque,
}: {
  rotulo: string;
  valor: number | null;
  vazio: string;
  nota: string;
  destaque?: boolean;
}) {
  return (
    <div className={`dp-rs-card${destaque ? ' dp-rs-card-balc' : ''}`}>
      <span className="dp-rs-rotulo">{rotulo}</span>
      {valor === null ? (
        <span className="dp-rs-vazio">{vazio}</span>
      ) : (
        <span className="dp-rs-valor">
          {String(valor)}
          <small>{valor === 1 ? 'unidade' : 'unidades'}</small>
        </span>
      )}
      <span className="dp-rs-nota">{nota}</span>
    </div>
  );
}

export function ResumoDaDivergencia({
  caso,
  investigacao,
  aoAtualizar,
  local = 'PRATELEIRA',
}: {
  caso: CasoDaFila;
  /** PRATELEIRA (Higor) ou ESTOQUE (Felipe): o mesmo resumo, com o que cada um precisa. */
  local?: 'PRATELEIRA' | 'ESTOQUE';
  /** undefined = buscando; null = não veio (o resumo segue com o que a fila trouxe). */
  investigacao: Investigacao | null | undefined;
  aoAtualizar: () => void;
}) {
  const r = montarResumo(caso, investigacao);
  const buscando = investigacao === undefined;
  const n = caso.numeros ?? null;
  const doEstoque = local === 'ESTOQUE';

  const notaAgora =
    r.hosAgora.valor === null
      ? buscando
        ? 'Lendo o HOS…'
        : 'Não deu para ler o HOS'
      : r.hosAgora.em
        ? `Atualizado às ${quandoBR(r.hosAgora.em)}`
        : 'Espelho do HOS';
  const notaContagem =
    r.hosNaContagem.valor === null
      ? 'Sem leitura do HOS na contagem'
      : r.hosNaContagem.fonte === 'GERACAO'
        ? `Lido na geração do Giro${r.hosNaContagem.em ? `, ${quandoBR(r.hosNaContagem.em)}` : ''}`
        : r.hosNaContagem.em
          ? `Contagem às ${quandoBR(r.hosNaContagem.em)}`
          : 'Na contagem original';
  const notaPrateleira = [
    r.prateleira.por ?? 'Contagem original',
    r.prateleira.em ? quandoBR(r.prateleira.em) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="dp-rs" aria-labelledby="dp-rs-titulo">
      <div className="dp-rs-cab">
        <h3 id="dp-rs-titulo">Resumo da divergência</h3>
        <button
          type="button"
          className="dp-rs-atualizar"
          onClick={aoAtualizar}
          disabled={buscando}
          aria-label="Atualizar o saldo do HOS"
        >
          <Icone nome="retry" tamanho={14} traco={2} />
          {buscando ? 'Lendo HOS…' : 'Atualizar HOS'}
        </button>
      </div>
      <div className="dp-rs-grade">
        <Numero
          rotulo="HOS agora"
          valor={r.hosAgora.valor}
          vazio={buscando ? '…' : 'Indisponível'}
          nota={notaAgora}
        />
        <Numero
          rotulo={r.hosNaContagem.fonte === 'GERACAO' ? 'HOS na geração' : 'HOS na contagem'}
          valor={r.hosNaContagem.valor}
          vazio="Indisponível"
          nota={notaContagem}
        />
        <Numero
          rotulo="Balconista — prateleira"
          valor={r.prateleira.valor}
          vazio="Não informado"
          nota={notaPrateleira}
          destaque
        />
        {doEstoque ? (
          <Numero
            rotulo="Esperado no estoque"
            valor={n?.esperadoNoEstoque ?? null}
            vazio="Indisponível"
            nota="HOS na contagem − prateleira"
            destaque
          />
        ) : (
          <Numero
            rotulo="Balconista — estoque"
            valor={r.estoque.valor}
            vazio="Não informado"
            nota="Informação não registrada"
            destaque
          />
        )}
      </div>

      {doEstoque && n && (
        <p className="dp-rs-mudou" role="note">
          {n.tipo === 'FALTOU_NA_REPOSICAO' ? (
            <span>
              Na reposição o balconista achou <b>{n.localizado ?? 0}</b> de <b>{n.solicitado}</b>{' '}
              pedidas.
            </span>
          ) : (
            <span>Nenhuma reposição foi pedida: a prateleira já tinha mais que o HOS.</span>
          )}
          <span>
            Prateleira (reconferência):{' '}
            {caso.felipeEncontrou !== null ? (
              <b>{caso.felipeEncontrou} conferidas</b>
            ) : (
              'aguardando — pode contar o estoque mesmo assim'
            )}
          </span>
        </p>
      )}

      {r.mudancaHos && (
        <p className="dp-rs-mudou" role="note">
          <span>
            HOS mudou desde a {r.hosNaContagem.fonte === 'GERACAO' ? 'geração' : 'contagem'}:{' '}
            <b>{comSinalUn(r.mudancaHos.delta)}</b>
          </span>
          <span>{r.mudancaHos.frase}</span>
          <span className="dp-rs-mudou-porque">
            {r.mudancaHos.explicacao ?? 'Buscando as movimentações do HOS…'}
          </span>
        </p>
      )}

      {doEstoque && n && caso.modoEstoque === 'BUSCA' && caso.alvo >= 1 ? (
        <div className="dp-rs-dif dp-rs-dif-ha">
          <span className="dp-rs-rotulo">Procurar no estoque</span>
          <strong className="dp-rs-dif-titulo">
            {caso.alvo} {caso.alvo === 1 ? 'unidade' : 'unidades'}
          </strong>
          <span className="dp-rs-dif-frase">
            O HOS tem mais de 6: procure só as que o balconista disse que não achou. O que importa é
            se o produto estava lá.
          </span>
          <span className="dp-rs-dif-tipo">
            Achou? É falha de reposição do balconista (conta para os 30%).
          </span>
        </div>
      ) : doEstoque && n && n.tipo === 'FALTOU_NA_REPOSICAO' ? (
        <div className={`dp-rs-dif dp-rs-dif-${n.naoComprovado ? 'ha' : 'zero'}`}>
          <span className="dp-rs-rotulo">Ainda não comprovado no estoque</span>
          <strong className="dp-rs-dif-titulo">
            {n.naoComprovado === null
              ? 'Sem leitura do HOS'
              : `${String(n.naoComprovado)} ${n.naoComprovado === 1 ? 'unidade' : 'unidades'}`}
          </strong>
          <span className="dp-rs-dif-frase">
            {n.esperadoNoEstoque === null
              ? 'Não há saldo do HOS da hora da contagem para dizer quanto deveria haver no estoque.'
              : `O HOS indicava ${String(n.esperadoNoEstoque)} no estoque e a busca da reposição comprovou ${String(n.localizado ?? 0)}.`}{' '}
            Conte <b>todas</b> as unidades do estoque, não só as que faltaram.
          </span>
          <span className="dp-rs-dif-tipo">Pendência de verificação — não é perda confirmada</span>
        </div>
      ) : (
        <div className={`dp-rs-dif dp-rs-dif-${r.diferenca.qtd === 0 ? 'zero' : 'ha'}`}>
          <span className="dp-rs-rotulo">Diferença identificada</span>
          <strong className="dp-rs-dif-titulo">{r.diferenca.titulo}</strong>
          <span className="dp-rs-dif-frase">{r.diferenca.frase}</span>
          <span className="dp-rs-dif-tipo">{r.diferenca.rotulo}</span>
          {r.fisicoTotal && (
            <span className="dp-rs-dif-fisico">
              <b>Estoque físico total:</b> {r.fisicoTotal}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
